import { useState, useEffect, useCallback } from 'react';
import { TrendingDown, TrendingUp, Filter, CheckCircle2, AlertTriangle, CheckCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { securityAPI } from '@/services/api';

const dateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const money = (v) => v == null ? '-' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => v == null ? '-' : `${Number(v).toFixed(2)}%`;

export default function MarginChangeLogPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, reduction_count: 0 });
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [filters, setFilters] = useState({ grn_number: '', date_from: '', date_to: '', only_reductions: false, pending_only: false });
  const [ackAllLoading, setAckAllLoading] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filters.grn_number) params.grn_number = filters.grn_number;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.only_reductions) params.only_reductions = true;
      if (filters.pending_only) params.pending_only = true;
      const res = await securityAPI.marginLogs(params);
      setLogs(res.data.items || []);
      setSummary({ total: res.data.total || 0, reduction_count: res.data.reduction_count || 0 });
    } catch (error) {
      if (error.response?.status === 403) {
        setForbidden(true);
        return;
      }
      toast({ title: 'Error', description: 'Failed to load margin change log', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (log) => {
    try {
      await securityAPI.acknowledgeMarginLog(log.id);
      toast({ title: 'Acknowledged', description: `${log.product_name} margin change marked as reviewed` });
      load();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Only Super Admin can acknowledge',
        variant: 'destructive'
      });
    }
  };

  const acknowledgeAll = async () => {
    if (!window.confirm('Mark ALL pending margin reductions as reviewed? The dashboard alert will clear instantly.')) return;
    try {
      setAckAllLoading(true);
      const res = await securityAPI.acknowledgeAllMarginLogs();
      toast({
        title: 'Marked reviewed',
        description: `${res.data.acknowledged_count} pending margin reduction${res.data.acknowledged_count === 1 ? '' : 's'} cleared`
      });
      load();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Only Super Admin can bulk acknowledge',
        variant: 'destructive'
      });
    } finally {
      setAckAllLoading(false);
    }
  };

  if (forbidden) {
    return (
      <div className="p-6" data-testid="margin-log-forbidden">
        <div className="max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h1 className="text-lg font-semibold text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Super Admin only
          </h1>
          <p className="text-sm text-amber-700 mt-2">
            The margin change log contains pricing-sensitive data. Ask an HQ Super Admin for access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="margin-log-page">
      <Toaster />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <TrendingDown className="w-6 h-6 text-red-500" /> Margin Change Log
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Every margin change from GRN entry and Product Master pricing - who changed it, from what to what, and when.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-gray-400">Total changes</p>
            <p className="text-2xl font-bold text-gray-800" data-testid="margin-total-count">{summary.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-red-500">Reductions pending review</p>
            <p className="text-2xl font-bold text-red-700" data-testid="margin-reduction-count">{summary.reduction_count}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-gray-200 mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs text-gray-500">GRN number</Label>
            <Input
              value={filters.grn_number}
              onChange={(e) => setFilters(f => ({ ...f, grn_number: e.target.value }))}
              placeholder="GRN2026..."
              className="w-44"
              data-testid="filter-grn-number"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
              className="w-40" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
              className="w-40" data-testid="filter-date-to" />
          </div>
          <Button
            variant={filters.only_reductions ? 'default' : 'outline'}
            onClick={() => setFilters(f => ({ ...f, only_reductions: !f.only_reductions }))}
            className={filters.only_reductions ? 'bg-red-500 hover:bg-red-600' : ''}
            data-testid="filter-reductions-toggle"
          >
            <Filter className="w-4 h-4 mr-2" /> Reductions only
          </Button>
          <Button
            variant={filters.pending_only ? 'default' : 'outline'}
            onClick={() => setFilters(f => ({ ...f, pending_only: !f.pending_only }))}
            className={filters.pending_only ? 'bg-amber-500 hover:bg-amber-600' : ''}
            data-testid="filter-pending-toggle"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> Pending review only
          </Button>
          {summary.reduction_count > 0 && (
            <Button
              onClick={acknowledgeAll}
              disabled={ackAllLoading}
              className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
              data-testid="ack-all-btn"
            >
              {ackAllLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
              Mark all reviewed ({summary.reduction_count})
            </Button>
          )}
          {(filters.grn_number || filters.date_from || filters.date_to || filters.only_reductions || filters.pending_only) && (
            <Button variant="ghost" onClick={() => setFilters({ grn_number: '', date_from: '', date_to: '', only_reductions: false, pending_only: false })}>
              Clear
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Margin</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Selling price</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Changed by</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">Loading...</TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No margin changes recorded</TableCell></TableRow>
              ) : logs.map(log => (
                <TableRow
                  key={log.id}
                  className={log.is_reduction && !log.acknowledged
                    ? 'bg-red-100 hover:bg-red-200/70'
                    : ''}
                  data-testid={`margin-log-row-${log.id}`}
                >
                  <TableCell
                    className={`text-sm text-gray-600 whitespace-nowrap ${
                      log.is_reduction && !log.acknowledged ? 'border-l-4 border-l-red-600' : ''
                    }`}
                  >
                    {dateTime(log.changed_at)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-gray-800">{log.product_name}</p>
                    <p className="text-xs text-gray-400">{log.sku}</p>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 font-medium">
                      {log.is_reduction
                        ? <TrendingDown className="w-4 h-4 text-red-500" />
                        : <TrendingUp className="w-4 h-4 text-emerald-500" />}
                      <span className="text-gray-500">{pct(log.old_margin_percent)}</span>
                      <span className="text-gray-400">→</span>
                      <span className={log.is_reduction ? 'text-red-600' : 'text-emerald-600'}>
                        {pct(log.new_margin_percent)}
                      </span>
                    </span>
                    {log.margin_delta != null && (
                      <span className={`text-xs ${log.is_reduction ? 'text-red-500' : 'text-emerald-500'}`}>
                        {log.margin_delta > 0 ? '+' : ''}{Number(log.margin_delta).toFixed(2)} pts
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{money(log.old_cost)} → {money(log.new_cost)}</TableCell>
                  <TableCell className="text-sm">{money(log.old_selling_price)} → {money(log.new_selling_price)}</TableCell>
                  <TableCell className="text-sm">
                    {log.source === 'grn' ? (
                      <span>
                        GRN
                        <span className="block text-xs text-gray-400">{log.grn_number}</span>
                      </span>
                    ) : 'Product Master'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{log.vendor_name || '-'}</TableCell>
                  <TableCell className="text-sm text-gray-600">{log.changed_by_name || '-'}</TableCell>
                  <TableCell className="text-right">
                    {log.is_reduction ? (
                      log.acknowledged ? (
                        <Badge className="bg-gray-100 text-gray-600 border border-gray-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Reviewed
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => acknowledge(log)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`acknowledge-btn-${log.id}`}>
                          <AlertTriangle className="w-3 h-3 mr-1" /> Acknowledge
                        </Button>
                      )
                    ) : (
                      <span className="text-xs text-gray-400">Increase</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
